import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getClassProduct } from "api/class";
import { getProductReviews } from "api/review";
import SubHeader from "components/common/SubHeader";
import ThumbnailCarousel from "components/shopping/ThumbnailCarousel";
import TabHeader from "components/common/TabHeader";
import NoData from "components/common/NoData";
import TuiViewer from "components/common/TuiViewer";
import useUserStore from "@store/useUserStore";
import LessonOptions from "components/store/LessonOptions";
import LoginBottomSheet from "components/common/LoginBottomSheet";
import { trackViewItem } from "utils/analytics";
import useShare from "hooks/useShare";
import shareIcon from "assets/images/icons/ic_share.svg";
import { postAddToCart } from "api/cart";
import { useSnackbar } from "contexts/SnackbarContext";
import ReviewList from "components/review/ReviewList";
import ProductInfo  from "components/store/ProductInfo";
import Seo from "components/common/Seo";

function ProductDetailPage() {
    const navigate = useNavigate();
    const { id } = useParams();

    const userInfo = useUserStore((state) => state.userInfo);
    const { share } = useShare();

    const [product, setProduct] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [mainImages, setMainImages] = useState([]);
    const [purchaseCnt, setPurchaseCnt] = useState(1);
    const [selectedTab, setSelectedTab] = useState("상세정보");
    const [showLoginBottomSheet, setShowLoginBottomSheet] = useState(false);
    const [lessonSelections, setLessonSelections] = useState({});
    const [optionPriceDelta, setOptionPriceDelta] = useState(0);
    const [reviewSummary, setReviewSummary] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [reviewHasMore, setReviewHasMore] = useState(false);
    const [reviewLoading, setReviewLoading] = useState(false);
    const { showSuccess, showError } = useSnackbar();

    // 리뷰 데이터는 페이지 로드 시점에 불러와서 캐싱한다.
    const fetchReviews = useCallback(async () => {
        if (!id) return;
        setReviewLoading(true);
        try {
            const data = await getProductReviews("lessons", id);
            const results = data.results || data || [];
            setReviews(results);
            setReviewSummary(data.summary || null);
            setReviewHasMore(!!data.next);
        } catch {
            // silent
        } finally {
            setReviewLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    const reviewCount = reviewSummary?.total_count ?? reviews.length;
    const reviewTabLabel = `리뷰(${reviewCount})`;
    const menus = ["상세정보", reviewTabLabel];

    // 리뷰 개수가 바뀌면 활성 탭 라벨도 동기화한다.
    useEffect(() => {
        if (selectedTab?.startsWith("리뷰") && selectedTab !== reviewTabLabel) {
            setSelectedTab(reviewTabLabel);
        }
    }, [reviewTabLabel, selectedTab]);


    useEffect(() => {
        getClassProduct(id).then((res) => {
            // 숨김/삭제 상품 등 단건 조회 실패 시 null → 안내 화면
            if (!res?.id) {
                setLoadFailed(true);
                return;
            }
            setLoadFailed(false);
            const mainImageUrls = (res.images ?? [])
                .filter((image) => image.is_thumbnail === true)
                .map((image) => image.image);
            setProduct(res);
            setMainImages(mainImageUrls);

            // GA4: 클래스 상세 조회 이벤트 추적
            trackViewItem({
                item_id: res.id,
                item_name: res.name,
                item_category: "lesson",
                price: res.discount_price || res.price,
            });
        });
    }, [id]);

    const TabContent = () => {
        const detailImages =
            product?.images?.filter((image) => image.image_type === "detail") ||
            [];
        if (selectedTab?.startsWith("리뷰")) {
            return (
                <ReviewList
                    summary={reviewSummary}
                    reviews={reviews}
                    hasMore={reviewHasMore}
                    loading={reviewLoading}
                    onLoadMore={fetchReviews}
                />
            );
        }
        switch (selectedTab) {
            case "상세정보":
                return (
                    <TuiViewer
                        className="class-detail-wrap"
                        htmlString={product.detailed_content}
                    />
                );
            case "문의":
                return (
                    <NoData
                        title="문의는 준비중입니다."
                        desc="빠른 시일 내에 준비하여 오픈하겠습니다."
                    />
                );
            default:
                return (
                    <TuiViewer
                        className="class-detail-wrap"
                        images={detailImages.map((image) => image.image)}
                    />
                );
        }
    };
    if (loadFailed) {
        return (
            <div className="page-product-detail">
                <SubHeader title="상품 상세" />
                {/* SubHeader 가 fixed 라 헤더 높이만큼 내려서 잘림을 방지한다 */}
                <div className="product-not-found">
                    <NoData
                        title="상품을 찾을 수 없습니다."
                        desc="판매가 종료되었거나 접근할 수 없는 상품입니다."
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="page-product-detail">
            <Seo
                title={product?.name}
                description={product?.name ? `${product.name} 강습을 버킷리스트클래스에서 예약하세요.` : undefined}
                image={mainImages?.[0]?.image || mainImages?.[0]}
            />
            <SubHeader
                title={product?.name || ""}
                menus={[{
                    name: '공유',
                    icon: shareIcon,
                    callback: () => share({ title: product?.name || '', appPath: `/class/detail/${id}` }),
                }]}
            />
            {product && (
                <div className="product-detail-header">
                    <ThumbnailCarousel images={mainImages} height={390} />
                    <ProductInfo
                        type="lesson"
                        product={product}
                        id={id}
                        purchaseCnt={purchaseCnt}
                        setPurchaseCnt={setPurchaseCnt}
                        externalPriceDelta={optionPriceDelta}
                    />
                    {product?.option_groups?.length > 0 && (
                        <div style={{ padding: "0 20px" }}>
                            <LessonOptions
                                optionGroups={product.option_groups}
                                selections={lessonSelections}
                                onChange={setLessonSelections}
                                onPriceChange={setOptionPriceDelta}
                            />
                        </div>
                    )}
                    <div
                        className="btn-wrap-flex gap-8"
                        style={{
                            padding: "0 20px",
                            marginBottom: "25px",
                        }}
                    >
                        <button
                            className="btn btn-outline-purple500 btn-large"
                            onClick={async () => {
                                if (!userInfo) {
                                    setShowLoginBottomSheet(true);
                                    return;
                                }
                                const requiredGroups = (product.option_groups || []).filter(
                                    (g) => g.is_active && g.is_required
                                );
                                const missingRequired = requiredGroups.some(
                                    (g) => !(lessonSelections[g.id]?.length > 0)
                                );
                                if (missingRequired) {
                                    showError("필수 옵션을 선택해주세요");
                                    return;
                                }
                                try {
                                    const lessonOptionSelections = {};
                                    Object.entries(lessonSelections).forEach(([key, val]) => {
                                        if (val.length > 0) lessonOptionSelections[key] = val;
                                    });
                                    await postAddToCart({
                                        item_type: "lesson_product",
                                        item_id: Number(id),
                                        quantity: purchaseCnt,
                                        lesson_option_selections: lessonOptionSelections,
                                    });
                                    showSuccess("장바구니에 담았습니다");
                                } catch (e) {
                                    showError("장바구니 담기에 실패했습니다");
                                }
                            }}
                        >
                            장바구니
                        </button>
                        <button
                            className={`btn btn-purple500 btn-large`}
                            onClick={() => {
                                if (!userInfo) {
                                    setShowLoginBottomSheet(true);
                                    return;
                                }
                                const requiredGroups = (product.option_groups || []).filter(
                                    (g) => g.is_active && g.is_required
                                );
                                const missingRequired = requiredGroups.some(
                                    (g) => !(lessonSelections[g.id]?.length > 0)
                                );
                                if (missingRequired) {
                                    showError("필수 옵션을 선택해주세요");
                                    return;
                                }
                                const lessonOptionSelections = {};
                                Object.entries(lessonSelections).forEach(([key, val]) => {
                                    if (val.length > 0) lessonOptionSelections[key] = val;
                                });
                                const optionSummary = (product.option_groups || [])
                                    .filter(
                                        (g) =>
                                            g.is_active &&
                                            lessonSelections[g.id]?.length > 0
                                    )
                                    .map((g) => ({
                                        groupName: g.name,
                                        selectedNames: g.values
                                            .filter((v) =>
                                                lessonSelections[g.id].includes(v.id)
                                            )
                                            .map((v) => v.name),
                                    }));
                                navigate(
                                    `/payment/lesson/${id}?purchaseCnt=${purchaseCnt}`,
                                    {
                                        state: {
                                            lessonOptionSelections,
                                            optionPriceDelta,
                                            optionSummary,
                                        },
                                    }
                                );
                            }}
                        >
                            구매하기
                        </button>
                    </div>
                </div>
            )}
            <TabHeader
                menus={menus}
                defaultTab={selectedTab}
                setSelectedTab={setSelectedTab}
            />
            <div className="tab-content-wrap">{product && <TabContent />}</div>
            <LoginBottomSheet
                isOpen={showLoginBottomSheet}
                onClose={() => setShowLoginBottomSheet(false)}
            />
        </div>
    );
}

export default ProductDetailPage;